import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cx } from './cx';
import { typography } from './Text';

export interface GfmMarkdownProps {
  /** Markdown source (GitHub-flavoured). */
  children: string;
  className?: string;
}

/**
 * Full-fidelity GitHub-flavoured markdown, rendered with `react-markdown` +
 * `remark-gfm` (ordered/nested lists, task lists, code fences, blockquotes,
 * tables, …) — what the lightweight in-house `Markdown` primitive can't do.
 * Every element is mapped to foundation tokens so it follows the palette, and
 * links reuse the `Link` primitive. No Backstage imports — Storybook-safe.
 */
export const GfmMarkdown = ({ children, className }: GfmMarkdownProps) => (
  <div
    className={cx(
      'flex min-w-0 flex-col gap-3 break-words text-fg-secondary',
      typography.body,
      className,
    )}
  >
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children: c }) => (
          <h1 className={cx(typography.title, 'text-fg')}>{c}</h1>
        ),
        h2: ({ children: c }) => (
          <h2 className={cx(typography.title, 'text-fg')}>{c}</h2>
        ),
        h3: ({ children: c }) => (
          <h3 className={cx(typography.label, 'font-semibold text-fg')}>{c}</h3>
        ),
        p: ({ children: c }) => <p className="leading-relaxed">{c}</p>,
        strong: ({ children: c }) => (
          <strong className="font-semibold text-fg">{c}</strong>
        ),
        ul: ({ children: c }) => (
          <ul className="ml-5 flex list-disc flex-col gap-1">{c}</ul>
        ),
        ol: ({ children: c }) => (
          <ol className="ml-5 flex list-decimal flex-col gap-1">{c}</ol>
        ),
        li: ({ children: c }) => <li className="leading-relaxed">{c}</li>,
        // A plain inline anchor (not the inline-flex Link primitive) so long
        // unbroken URLs wrap inside the container instead of overflowing.
        a: ({ href, children: c }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-brand-text underline [overflow-wrap:anywhere]"
          >
            {c}
          </a>
        ),
        code: ({ inline, children: c }) =>
          inline ? (
            <code className="rounded bg-muted-strong px-1 py-0.5 font-mono text-[0.85em] text-fg">
              {c}
            </code>
          ) : (
            <code className="font-mono text-[0.85em]">{c}</code>
          ),
        pre: ({ children: c }) => (
          <pre className="overflow-x-auto rounded-lg border border-default bg-muted-strong p-3 text-fg">
            {c}
          </pre>
        ),
        blockquote: ({ children: c }) => (
          <blockquote className="border-l-2 border-strong pl-3 text-fg-tertiary">
            {c}
          </blockquote>
        ),
        hr: () => <hr className="border-default" />,
        table: ({ children: c }) => (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">{c}</table>
          </div>
        ),
        th: ({ children: c }) => (
          <th className="border border-default px-2 py-1 font-semibold text-fg">
            {c}
          </th>
        ),
        td: ({ children: c }) => (
          <td className="border border-default px-2 py-1">{c}</td>
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  </div>
);
